/**
 * AppointmentsTilesWidget — the Penpot "Work Orders Section": a grid of
 * MetricTiles. Two views, switched by a tab control (mirrors the calendar's
 * "View by Appointment Type" toggle):
 *
 *   "Appointments"     → one tile per appointment STATUS + a Total tile.
 *                        Labels/colors from the bootstrap catalog statuses.
 *   "Appointment Type" → one tile per active appointment TYPE (mirrors the
 *                        calendar legend) + a "No Type" tile + Total.
 *                        Data from /api/v2/dashboard/appointments-by-type.
 *
 * Both views use the same TileGrid; the card body scrolls when a business has
 * many types, so the widget never grows past its grid slot.
 */

import * as React from "react";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { MetricTile, RefreshButton, TileGrid, WidgetCard } from "components/dashboard";
import {
  useAppointmentsSummary,
  useAppointmentsByType,
  formatQueryError,
} from "../hooks";
import type {
  AppointmentsByType,
  AppointmentsSummary,
  LayoutItem,
  StatusEntry,
  WidgetConfig,
} from "../types";

interface Props {
  widget: WidgetConfig;
  layout: LayoutItem;
}

type View = "status" | "type";

const TOTAL_ACCENT = "34,197,94"; // tailwind green-500 — matches the Penpot total tile
const NO_TYPE_ACCENT = "156,163,175"; // gray-400 — untyped appointments

interface Tile {
  key: string;
  label: string;
  value: number;
  color: string | null;
}

interface Labels {
  total: string;
  noType: string;
  tabStatus: string;
  tabType: string;
}

export function AppointmentsTilesWidget({ widget, layout }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [view, setView] = React.useState<View>("status");

  const labels: Labels = {
    total: t("views.home.dashboard.totalAppointments", "Total Appointments"),
    noType: t("views.home.dashboard.noType", "No Type"),
    tabStatus: t("views.home.dashboard.tabAppointments", "Appointments"),
    tabType: t("views.home.dashboard.tabAppointmentType", "Appointment Type"),
  };

  const summary = useAppointmentsSummary();
  const byType = useAppointmentsByType(view === "type");

  const isLoading = view === "status" ? summary.isLoading : byType.isLoading;
  const error = view === "status" ? summary.error : byType.error;

  const statuses = (widget.display.statuses as StatusEntry[] | undefined) ?? [];

  const tiles =
    view === "status"
      ? buildStatusTiles(statuses, summary.data, labels.total)
      : buildTypeTiles(byType.data, labels);

  return (
    <WidgetCard
      title={widget.title}
      w={widget.defaultSize.w}
      h={widget.defaultSize.h}
      x={layout.x}
      y={layout.y}
      loading={isLoading}
      error={error ? formatQueryError(error) : null}
      headerRight={
        <div className="flex items-center gap-2">
          <ViewTabs
            value={view}
            onChange={setView}
            statusLabel={labels.tabStatus}
            typeLabel={labels.tabType}
          />
          <RefreshButton
            onClick={() =>
              queryClient.invalidateQueries([
                "dashboard",
                view === "status" ? "appointments-summary" : "appointments-by-type",
              ])
            }
          />
        </div>
      }
    >
      <TileGrid cols={5}>
        {tiles.map((tile) => (
          <MetricTile
            key={tile.key}
            label={tile.label}
            value={tile.value}
            color={tile.color}
            loading={isLoading}
          />
        ))}
      </TileGrid>
    </WidgetCard>
  );
}

function buildStatusTiles(
  statuses: StatusEntry[],
  data: AppointmentsSummary | undefined,
  totalLabel: string,
): Tile[] {
  const tiles: Tile[] = statuses.map((s) => ({
    key: `s-${s.id}`,
    label: s.label,
    value: data?.statusCounts.find((b) => b.statusId === s.id)?.count ?? 0,
    color: s.color,
  }));
  const total = data ? data.statusCounts.reduce((sum, b) => sum + b.count, 0) : 0;
  tiles.push({ key: "total", label: totalLabel, value: total, color: TOTAL_ACCENT });
  return tiles;
}

function buildTypeTiles(data: AppointmentsByType | undefined, labels: Labels): Tile[] {
  const tiles: Tile[] = (data?.types ?? []).map((ty) => ({
    key: `t-${ty.typeId}`,
    label: ty.label,
    value: ty.count,
    color: ty.color,
  }));
  const untyped = data?.untyped ?? 0;
  tiles.push({ key: "no-type", label: labels.noType, value: untyped, color: NO_TYPE_ACCENT });
  const total = (data?.types ?? []).reduce((sum, ty) => sum + ty.count, 0) + untyped;
  tiles.push({ key: "total", label: labels.total, value: total, color: TOTAL_ACCENT });
  return tiles;
}

function ViewTabs({
  value,
  onChange,
  statusLabel,
  typeLabel,
}: {
  value: View;
  onChange: (v: View) => void;
  statusLabel: string;
  typeLabel: string;
}) {
  const options: Array<{ key: View; label: string }> = [
    { key: "status", label: statusLabel },
    { key: "type", label: typeLabel },
  ];
  return (
    <div className="inline-flex rounded-full bg-gray-100 p-0.5 text-[10px]">
      {options.map((opt) => (
        <button
          key={opt.key}
          type="button"
          onClick={() => onChange(opt.key)}
          className={`px-2 py-0.5 rounded-full transition-colors whitespace-nowrap ${
            value === opt.key
              ? "bg-white text-gray-700 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
